import { spawnStateFor } from '../../core/ProjectileSim.js';
/**
 * GameEvents - Core Game State Socket Event Handlers
 *
 * Handles events related to core game state:
 * - spawn: Player spawned into game
 * - state: Server state snapshot (delta compressed)
 * - projectileSpawned: New projectile created
 * - zoneState: Zone shrinking state updates
 * - zoneDamage: Zone damage tick notifications
 */
import { CollisionUtils } from '../../../shared/CollisionUtils.js';
import { Movement } from '../../../shared/Movement.js';
import { LocalPlayer } from '../../LocalPlayer.js';
import { RemotePlayer } from '../../RemotePlayer.js';
import { showNotification } from '../../client.js';
import { configRegistry } from '../../core/ConfigRegistry.js';
import { GameState } from '../../core/GameState.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const gameEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const gameEventsLogger = ClientLogger.getLogger('network') || gameEventsLoggerFallback;

/** Who fired, for one-way objects (firewalls) in the tracer termination raycast. */
function shooterOptions(gameState, ownerId) {
  return { shooterId: ownerId, shooterTeam: gameState.players.get(ownerId)?.team ?? null };
}

const GameEvents = {
  /**
   * Register all game state event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.hideSplashScreen - Hide splash and enter game
   * @param {Function} callbacks.getDefaultWeapon - Get default weapon
   * @param {Function} callbacks.createLocalPlayer - Create LocalPlayer instance
   * @param {Function} callbacks.createRemotePlayer - Create RemotePlayer instance
   * @param {Function} callbacks.loadWorldObjectImage - Load world object image
   * @param {Function} callbacks.playOtherPlayerShootSound - Play sound for other player shots
   * @param {Function} callbacks.initializePlayerSounds - Initialize player sound manager
   * @param {Function} callbacks.syncLocalAccessors - Sync local accessor variables with gameState
   */
  register(socket, gameState, callbacks) {
    const {
      hideSplashScreen,
      getDefaultWeapon,
      createLocalPlayer,
      createRemotePlayer,
      loadWorldObjectImage,
      playOtherPlayerShootSound,
      initializePlayerSounds,
      syncLocalAccessors
    } = callbacks;

    // Game configurations from server (abilities, pickups, settings)
    // Sent once on connection to synchronize client with server configs
    socket.on('gameConfigs', (data) => {
      if (typeof configRegistry !== 'undefined') {
        configRegistry.load(data);
      } else {
        gameEventsLogger.warn('[GameEvents] ConfigRegistry not available, configs not loaded');
      }
    });

    // Player spawned into game
    socket.on('spawn', (data) => {
      // Update gameState directly for state that needs to persist
      gameState.localId = data.id;
      // Show predicted-only misses from the first shot, including after a lobby reset.
      gameState.hitregDebugActive = !!gameState.abilitiesConfig?.testingMode?.enabled;

      // Apply server's player physics config for accurate client-side prediction
      if (data.playerConfig?.movement) {
        GameState.MOVEMENT.speedPxPerSec = data.playerConfig.movement.speedPxPerSec;
        GameState.MOVEMENT.radius = data.playerConfig.movement.radius;
      }
      if (data.playerConfig?.drift) {
        GameState.DRIFT = Movement.driftParams(data.playerConfig.drift, GameState.FIXED_DT);
      }

      // Apply map config from server (centralized in MapSystem)
      if (data.mapConfig) {
        gameState.mapConfig = data.mapConfig;
        gameState.mapWidth = data.mapConfig.width;
        gameState.mapHeight = data.mapConfig.height;
        gameEventsLogger.info(`✓ Map config received: ${data.mapConfig.name} (${gameState.mapWidth}x${gameState.mapHeight})`);
      }

      // Apply game mode config from server
      if (data.gameModeConfig) {
        gameState.gameModeConfig = data.gameModeConfig;
        gameEventsLogger.info(`✓ Game mode config received: ${data.gameModeConfig.name || data.gameModeConfig.id} (showLeaderboard: ${data.gameModeConfig.showLeaderboard})`);
      }

      gameState.team = Number.isInteger(data.team) ? data.team : null;
      gameState.teams = Array.isArray(data.teams) ? data.teams : null;
      gameState.modeState = null;

      // Initialize world objects from server state (server-authoritative)
      gameEventsLogger.debug('[GameEvents] 🌍 Spawn data received:', {
        hasWorldObjects: !!data.worldObjects,
        worldObjectsType: Array.isArray(data.worldObjects) ? 'array' : typeof data.worldObjects,
        worldObjectsLength: data.worldObjects?.length,
        sampleObjects: data.worldObjects?.slice(0, 3)
      });

      if (data.worldObjects) {
        gameState.worldObjects.clear();
        for (const obj of data.worldObjects) {
          gameState.worldObjects.set(obj.id, obj);
          if (obj.objectType === 'lootContainer') {
            gameState.lootContainers.set(obj.id, obj);
          }

          // Load image for this world object if not already cached
          if (loadWorldObjectImage) {
            loadWorldObjectImage(obj);
          }
        }
        gameEventsLogger.info(`✓ Initialized ${gameState.worldObjects.size} world objects from server`);
        gameEventsLogger.debug('[GameEvents] 📦 WorldObjects sample:', Array.from(gameState.worldObjects.entries()).slice(0, 3));
        gameState.rebuildColliders();
      } else {
        gameEventsLogger.warn('[GameEvents] ⚠️ No worldObjects in spawn data!');
      }

      // Create LocalPlayer with proper config
      const playerConfig = {
        id: data.id,
        name: gameState.playerName || 'Player',
        x: data.x,
        y: data.y,
        cosmetics: data.cosmetics || { equippedSkin: 'default' },
        equippedWeapon: getDefaultWeapon ? getDefaultWeapon() : null
      };

      // Check if player already exists (respawn case) - preserve stats
      const existingPlayer = gameState.players.get(data.id);
      const existingStats = existingPlayer?.stats;

      const localPlayer = createLocalPlayer
        ? createLocalPlayer(playerConfig)
        : new LocalPlayer(playerConfig);

      // Restore accumulated stats from before respawn
      if (existingStats) {
        localPlayer.stats = existingStats;
      }

      // Ensure player is marked as alive on spawn/respawn
      localPlayer.isDead = false;

      gameState.players.set(gameState.localId, localPlayer);
      if (typeof gameState.updateScoreboardPlayer === 'function') {
        gameState.updateScoreboardPlayer(gameState.localId, {
          name: localPlayer.name,
          kills: localPlayer.stats?.kills || 0,
          deaths: localPlayer.stats?.deaths || 0
        });
      }

      // Reset local ability UI state until server sync arrives
      if (gameState.abilityState) {
        gameState.abilityState.cooldowns = {};
        gameState.abilityState.globalCooldowns = { utility: 0, movement: 0 };
        gameState.abilityState.activeEffects = {};
      }

      // Store room ID
      gameState.currentRoomId = data.roomId;

      // Initialize player sound manager with player config
      if (initializePlayerSounds && data.playerConfig) {
        initializePlayerSounds(data.playerConfig);
      }

      // Sync local accessor variables with gameState
      if (syncLocalAccessors) {
        syncLocalAccessors();
      }

      // Hide splash screen and enter game
      if (hideSplashScreen) {
        hideSplashScreen();
      }
    });

    // Server rebound us into our live match after a transport drop. Unlike
    // 'spawn' this is a resume: the entity kept its position, health and
    // inventory, so we re-seed the world and restart prediction from the
    // server's position rather than building a new player.
    socket.on('rejoin', (data) => {
      gameState.rejoinPending = false;
      clearTimeout(gameState.rejoinTimer);
      gameState.rejoinTimer = null;
      gameState.localId = data.id;
      gameState.currentRoomId = data.roomId;

      if (data.playerConfig?.movement) {
        GameState.MOVEMENT.speedPxPerSec = data.playerConfig.movement.speedPxPerSec;
        GameState.MOVEMENT.radius = data.playerConfig.movement.radius;
      }
      if (data.playerConfig?.drift) {
        GameState.DRIFT = Movement.driftParams(data.playerConfig.drift, GameState.FIXED_DT);
      }
      if (data.mapConfig) {
        gameState.mapConfig = data.mapConfig;
        gameState.mapWidth = data.mapConfig.width;
        gameState.mapHeight = data.mapConfig.height;
      }
      if (data.gameModeConfig) {
        gameState.gameModeConfig = data.gameModeConfig;
      }
      gameState.team = Number.isInteger(data.team) ? data.team : null;
      gameState.teams = Array.isArray(data.teams) ? data.teams : null;
      gameState.modeState = null;
      if (data.worldObjects) {
        gameState.worldObjects.clear();
        gameState.lootContainers.clear();
        for (const obj of data.worldObjects) {
          gameState.worldObjects.set(obj.id, obj);
          if (obj.objectType === 'lootContainer') gameState.lootContainers.set(obj.id, obj);
          if (loadWorldObjectImage) loadWorldObjectImage(obj);
        }
        gameState.rebuildColliders();
      }

      // Keep our LocalPlayer but restart prediction from the server's position:
      // the server reset our input sequencing to 0 on rebind.
      const me = gameState.players.get(data.id);
      if (me) {
        me.x = data.x; me.y = data.y; me.visualOffsetX = 0; me.visualOffsetY = 0;
        me.vx = 0; me.vy = 0;
        me.inputHistory = [];
        me.inputSeq = 0;
        delete me.lastShotSeq;
        delete me.lastSwingSeq;
        me.lastAckSeq = 0;
        me.serverHistory = [];
      }
      gameState.inputState.aimSeq = 0;

      // Remote players and projectiles refill from the next (full) snapshot
      for (const [id, p] of gameState.players) {
        if (id !== data.id) p.serverHistory = [];
      }
      gameState.projectiles.length = 0;
      gameState.serverProjectileState.clear();
      gameState.clockOffset = null; // re-estimate the server clock

      if (syncLocalAccessors) syncLocalAccessors();
      if (hideSplashScreen) hideSplashScreen(); // also re-syncs isInGame
      if (typeof showNotification === 'function') showNotification('Reconnected', 'success');
      gameEventsLogger.info('Rejoined match', { roomId: data.roomId });
    });

    // The server could not put us back in our match (grace expired, room gone)
    socket.on('rejoinFailed', (info) => {
      gameEventsLogger.warn('Rejoin failed', info);
      gameState.rejoinPending = false;
      clearTimeout(gameState.rejoinTimer);
      gameState.rejoinTimer = null;
      if (typeof callbacks.resetToLobbyAfterReconnect === 'function') {
        callbacks.resetToLobbyAfterReconnect();
      }
    });

    // Server state snapshot
    socket.on('state', (snapshot) => {
      const receiveTime = performance.now();
      gameState.observeServerTime(snapshot.serverTime, receiveTime);
      this._handleStateSnapshot(snapshot, receiveTime, gameState, callbacks);
    });

    // Dynamic world objects (firewalls): appear, change (hp) and disappear mid-match.
    socket.on('worldObjectUpsert', (data) => {
      const obj = data?.worldObject;
      if (!obj?.id) return;
      gameState.worldObjects.set(obj.id, obj);
      gameState.rebuildColliders?.();
    });
    socket.on('worldObjectRemoved', (data) => {
      if (!data?.objectId || !gameState.worldObjects.delete(data.objectId)) return;
      gameState.rebuildColliders?.();
    });

    // Projectile spawned
    socket.on('projectileSpawned', (proj) => {
      // Our own shot, resolved during the server's lag-compensation catch-up: the
      // predicted tracer is already on screen and the damage events will follow.
      if (proj.resolved && proj.ownerId === gameState.localId) return;
      const exists = gameState.projectiles.some(p => p.id === proj.id);
      if (!exists) {
        const receiveTime = performance.now();
        const spawn = spawnStateFor(proj, receiveTime);

        // Others' shots that had already flown (catch-up) get a streak from the gun,
        // so a shot that hit in catch-up is still seen.
        if (spawn.streak && proj.ownerId !== gameState.localId) {
          gameState.shotStreaks.push({ ...spawn.streak, color: proj.tracerColor, startTime: receiveTime });
        }

        if (!proj.resolved) {
          let terminationTime = spawn.terminationTime;
          if (terminationTime === null) {
            const projectileLifetime = proj.projectileLifetime || GameState.PROJECTILE_LIFETIME;
            const maxTravelTime = projectileLifetime / 1000;
            const collision = typeof CollisionUtils !== 'undefined'
              ? CollisionUtils.getFirstCollisionPoint(spawn.x, spawn.y, spawn.x + proj.vx * maxTravelTime, spawn.y + proj.vy * maxTravelTime, gameState.worldObjects, shooterOptions(gameState, proj.ownerId))
              : null;
            terminationTime = collision ? collision.t * maxTravelTime * 1000 : projectileLifetime;
          }
          gameState.serverProjectileState.set(proj.id, {
            receiveTime: spawn.t0,
            x: spawn.x,
            y: spawn.y,
            vx: proj.vx,
            vy: proj.vy,
            terminationTime
          });
          gameState.projectiles.push(proj);
        }

        if (proj.ownerId) {
          const shotAt = performance.now();
          gameState.playerLastShotTime.set(proj.ownerId, shotAt);
          gameState.recordShot(proj.ownerId, spawn.x, spawn.y, shotAt);
        }

        // For our own shots, mark the predicted projectile to be removed soon
        if (proj.ownerId === gameState.localId && gameState.predictedProjectiles.length > 0) {
          const predicted = gameState.predictedProjectiles[0];
          if (predicted && !predicted.serverConfirmed) {
            predicted.serverConfirmed = true;
            predicted.serverConfirmedAt = performance.now();
            predicted.serverId = proj.id;
          }
        }
        // Play sound for other players' shots if they're nearby
        else if (proj.ownerId !== gameState.localId && playOtherPlayerShootSound) {
          playOtherPlayerShootSound(spawn.x, spawn.y, proj.weaponId);
        }
      }
    });

    socket.on('zoneState', (zoneData) => {
      if (!zoneData || typeof gameState.setZoneState !== 'function') {
        return;
      }
      gameState.setZoneState(zoneData);
    });

    socket.on('zoneFinished', () => {
      if (typeof gameState.setZoneState !== 'function') {
        return;
      }
      gameState.setZoneState({
        timeRemaining: 0
      });
    });

    socket.on('zoneDamage', (data) => {
      if (typeof gameState.recordZoneDamage === 'function') {
        gameState.recordZoneDamage(data);
      } else {
        gameState.lastZoneDamage = data;
      }
    });
  },

  /**
   * Handle server state snapshot
   * @private
   */
  _handleStateSnapshot(snapshot, receiveTime, gameState, callbacks) {
    const { getDefaultWeapon, createRemotePlayer } = callbacks;
    const serverPlayerIds = new Set();

    if (snapshot.mode !== undefined) {
      gameState.modeState = snapshot.mode;
    }

    if (typeof snapshot.alivePlayers === 'number') {
      gameState.alivePlayers = snapshot.alivePlayers;
    }

    // Handle delta compression - new projectiles are added, not replaced
    for (const proj of (snapshot.projectiles || [])) {
      if (!gameState.serverProjectileState.has(proj.id)) {
        // Precompute termination point for this projectile
        const projectileLifetime = GameState.PROJECTILE_LIFETIME;
        const maxTravelTime = projectileLifetime / 1000;
        const maxEndX = proj.x + proj.vx * maxTravelTime;
        const maxEndY = proj.y + proj.vy * maxTravelTime;

        // Find first collision along projectile's path
        const collision = typeof CollisionUtils !== 'undefined'
          ? CollisionUtils.getFirstCollisionPoint(proj.x, proj.y, maxEndX, maxEndY, gameState.worldObjects, shooterOptions(gameState, proj.ownerId))
          : null;

        // Calculate termination time relative to receiveTime
        let terminationTime = projectileLifetime;
        if (collision) {
          terminationTime = collision.t * maxTravelTime * 1000;
        }

        gameState.serverProjectileState.set(proj.id, {
          receiveTime,
          x: proj.x,
          y: proj.y,
          vx: proj.vx,
          vy: proj.vy,
          terminationTime: terminationTime
        });
        gameState.projectiles.push(proj);
      }
    }

    // Handle explicitly removed projectiles (from delta compression)
    if (snapshot.removedProjectiles) {
      for (const projId of snapshot.removedProjectiles) {
        gameState.serverProjectileState.delete(projId);
        const idx = gameState.projectiles.findIndex(p => p.id === projId);
        if (idx !== -1) {
          gameState.projectiles.splice(idx, 1);
        }
      }
    }

    // Client-side projectile cleanup based on lifetime
    const PROJECTILE_LIFETIME = GameState.PROJECTILE_LIFETIME;
    for (let i = gameState.projectiles.length - 1; i >= 0; i--) {
      const state = gameState.serverProjectileState.get(gameState.projectiles[i].id);
      if (!state || (receiveTime - state.receiveTime) > PROJECTILE_LIFETIME) {
        if (state) gameState.serverProjectileState.delete(gameState.projectiles[i].id);
        gameState.projectiles.splice(i, 1);
      }
    }

    // Reconcile predicted projectiles with server projectiles
    for (let i = gameState.predictedProjectiles.length - 1; i >= 0; i--) {
      const pred = gameState.predictedProjectiles[i];
      const predTimeSince = (receiveTime - pred.createdAt) / 1000;
      const predX = pred.initialX + pred.vx * predTimeSince;
      const predY = pred.initialY + pred.vy * predTimeSince;

      for (const serv of gameState.projectiles) {
        if (pred.ownerId !== serv.ownerId) continue;

        const state = gameState.serverProjectileState.get(serv.id);
        if (!state) continue;

        const servTimeSince = (receiveTime - state.receiveTime) / 1000;
        const servX = state.x + state.vx * servTimeSince;
        const servY = state.y + state.vy * servTimeSince;

        const dx = predX - servX;
        const dy = predY - servY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 100) {
          gameState.predictedProjectiles.splice(i, 1);
          break;
        }
      }
    }

    // Update players
    for (const state of snapshot.players) {
      serverPlayerIds.add(state.id);
      let player = gameState.players.get(state.id);

      if (!player) {
        const playerConfig = {
          id: state.id,
          name: state.name || 'Player',
          x: state.x ?? 0,
          y: state.y ?? 0,
          cosmetics: state.cosmetics,
          equippedWeapon: getDefaultWeapon ? getDefaultWeapon() : null
        };

        player = state.id === gameState.localId
          ? (createRemotePlayer ? new LocalPlayer(playerConfig) : new LocalPlayer(playerConfig))
          : (createRemotePlayer ? createRemotePlayer(playerConfig) : new RemotePlayer(playerConfig));
        gameState.players.set(state.id, player);
      }

      // Delta compression: merge partial state with existing player
      player.updateFromServer(state, snapshot.keyframe, snapshot.serverTime);

      // Track alive/dead state for rendering/leaderboard
      if (player.health && typeof player.health.currentHealth === 'number') {
        player.isDead = player.health.currentHealth <= 0;
      }

      if (typeof gameState.updateScoreboardPlayer === 'function') {
        gameState.updateScoreboardPlayer(player.id, {
          name: player.name || state.name,
          kills: player.stats?.kills ?? 0,
          deaths: player.stats?.deaths ?? 0,
          team: state.team
        });
      }

      if (player.isLocal) {
        player.reconcile(state, GameState.RECONCILE_THRESHOLD);
      }
    }

    // Decoys (spec D3/D4): the array is the complete visible set every broadcast,
    // so one that stops arriving — out of view, shot, replaced — simply goes away.
    // They live in their own map: a decoy is never a player, on the scoreboard or
    // anywhere else that counts people.
    if (Array.isArray(snapshot.decoys)) {
      const seenDecoys = new Set();
      for (const state of snapshot.decoys) {
        seenDecoys.add(state.id);
        let decoy = gameState.decoys.get(state.id);
        if (!decoy) {
          const decoyConfig = {
            id: state.id,
            name: state.name || 'Player',
            x: state.x ?? 0,
            y: state.y ?? 0,
            cosmetics: state.cosmetics,
            equippedWeapon: getDefaultWeapon ? getDefaultWeapon() : null
          };
          decoy = createRemotePlayer ? createRemotePlayer(decoyConfig) : new RemotePlayer(decoyConfig);
          decoy.isDecoy = true;
          gameState.decoys.set(state.id, decoy);
        }
        decoy.updateFromServer(state, true, snapshot.serverTime);
      }
      for (const id of [...gameState.decoys.keys()]) {
        if (!seenDecoys.has(id)) gameState.decoys.delete(id);
      }
    }

    // Handle explicitly removed players (from delta compression)
    if (snapshot.removedPlayers) {
      for (const playerId of snapshot.removedPlayers) {
        if (playerId !== gameState.localId) {
          gameState.players.delete(playerId);
        }
      }
    }

    // On keyframes only, remove players not in snapshot
    if (snapshot.keyframe) {
      for (const [playerId] of gameState.players.entries()) {
        if (!serverPlayerIds.has(playerId) && playerId !== gameState.localId) {
          gameState.players.delete(playerId);
        }
      }
    }
  }
};

// Export for use in other modules
// For now, GameEvents is a global object loaded via script tag

export { gameEventsLoggerFallback, gameEventsLogger, GameEvents };
